import { post, get, patch, deleted, Statementget, put } from "services";
import routes from "./routes";

const accountReportsCache = new Map();
const ACCOUNT_REPORTS_CACHE_TTL_MS = 5 * 60 * 1000;
const coaSubAccountsCache = new Map();
const chartOfAccountLabelCache = new Map();
const COA_SUB_ACCOUNTS_CACHE_TTL_MS = 60 * 1000;
const COA_LABEL_CACHE_TTL_MS = 30 * 60 * 1000;

const getCacheKeyFromParams = (params = {}) => {
  if (!params || typeof params !== "object") return "all";
  const sortedKeys = Object.keys(params).sort();
  const normalized = sortedKeys.reduce((acc, key) => {
    acc[key] = params[key];
    return acc;
  }, {});
  return JSON.stringify(normalized);
};

const clearAccountingCaches = () => {
  accountReportsCache.clear();
  coaSubAccountsCache.clear();
  chartOfAccountLabelCache.clear();
};

const FinanceServices = {
  getUnits: async () => {
    const data = await get(routes.getUnits);
    return data;
  },
  getMajorCategories: async () => {
    const data = await get(routes.getMajorCategories);
    return data;
  },
  getReceipts: async (params) => {
    const data = await get(routes.getReceipts, params);
    return data;
  },
  getShippingAging: async (params) => {
    const data = await get(routes.getShippingAging, params);
    return data;
  },
  getVoucherPreview: async (params) => {
    const data = await get(routes.getVoucherPreview, params);
    return data;
  },
  getReceiptDetail: async (params) => {
    const data = await get(routes.getReceiptDetail, params);
    return data;
  },
  getReceiptPreview: async (params) => {
    const data = await get(routes.getReceiptPreview, params);
    return data;
  },

  getShippingProfit: async (params) => {
    const data = await get(routes.getShippingProfit, params);
    return data;
  },
  getSubCategories: async (params) => {
    const data = await get(routes.getSubCategories, params);
    return data;
  },
  getShippingReceiving: async (params) => {
    const data = await get(routes.getShippingReceiving, params);
    return data;
  },
  getVouchers: async (params) => {
    const data = await get(routes.getVouchers, params);
    return data;
  },
  getVoucherDetails: async (params) => {
    const data = await get(routes.getVoucherDetails, params);
    return data;
  },
  deleteVoucher: async (params) => {
    const data = await deleted(routes.deleteVoucher, params);
    return data;
  },
  deleteReceipt: async (params) => {
    const data = await deleted(routes.deleteReceipt, params);
    return data;
  },
  deleteIFTV: async (params) => {
    const data = await deleted(routes.deleteIFTV, params);
    return data;
  },
  getShippingPaymentAccounts: async (params) => {
    const data = await get(routes.getPaymentAccountsShipping, params);
    return data;
  },
  deleteJournalVoucher: async (params) => {
    const data = await deleted(routes.deleteJournalVoucher, params);
    return data;
  },
  getAccountCode: async (params) => {
    const data = await get(routes.getAccountCode, params);
    return data;
  },
  getAccountBySubCategory: async (params) => {
    const data = await get(routes.getAccountBySubCategory, params);
    return data;
  },
  getAccounts: async (params) => {
    const data = await get(routes.getAccounts, params);
    return data;
  },
  getAccountsDropDown: async (params) => {
    const data = await get(routes.getAccountsDropDown, params);
    return data;
  },
  createAccount: async (obj) => {
    const data = await post(routes.createAccount, obj);
    clearAccountingCaches();
    return data;
  },

  ReceiptVoucher: async (obj) => {
    const data = await post(routes.ReceiptVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  ReceiptVoucherV2: async (obj) => {
    const data = await post(routes.ReceiptVoucherV2, obj);
    clearAccountingCaches();
    return data;
  },
  updateAccount: async (obj) => {
    const data = await patch(routes.updateAccount, obj);
    clearAccountingCaches();
    return data;
  },
  getAccountsApprovals: async (params) => {
    const data = await get(routes.getAccountsApprovals, params);
    return data;
  },
  approveAccount: async (obj) => {
    const data = await post(routes.approveAccount, obj);
    clearAccountingCaches();
    return data;
  },
  PaymentVoucher: async (obj) => {
    const data = await post(routes.PaymentVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  PaymentVoucherV2: async (obj) => {
    const data = await post(routes.PaymentVoucherV2, obj);
    clearAccountingCaches();
    return data;
  },
  UpdatePaymentVoucher: async (obj) => {
    const data = await patch(routes.updatePaymentVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  ReversePaymentVoucher: async (obj) => {
    const data = await patch(routes.reversePaymentVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  ReverseReceiptVoucher: async (obj) => {
    const data = await patch(routes.reverseReceiptVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  ReverseVaultTopup: async (obj) => {
    const data = await patch(routes.reverseVaultTopUp, obj);
    clearAccountingCaches();
    return data;
  },
  ReverseJV: async (obj) => {
    const data = await patch(routes.reverseJV, obj);
    clearAccountingCaches();
    return data;
  },
  ReverseIFTVoucher: async (obj) => {
    const data = await patch(routes.ReverseIFTVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  getChartOfAccount: async (params) => {
    const data = await get(routes.getChartOfAccount, params);
    return data;
  },
  getCOASubAccounts: async (params) => {
    const cacheKey = getCacheKeyFromParams(params);
    const cached = coaSubAccountsCache.get(cacheKey);

    if (
      cached &&
      Date.now() - cached.timestamp < COA_SUB_ACCOUNTS_CACHE_TTL_MS
    ) {
      return cached.data;
    }

    const data = await get(routes.getCOASubAccounts, params);
    coaSubAccountsCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  },
  getChartOfAccountLabel: async (params) => {
    const cacheKey = getCacheKeyFromParams(params);
    const cached = chartOfAccountLabelCache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < COA_LABEL_CACHE_TTL_MS) {
      return cached.data;
    }

    const data = await get(routes.getChartOfAccountLabel, params);
    chartOfAccountLabelCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  },
  getChartOfAccountSubAccount: async (params) => {
    const data = await get(routes.getChartOfAccountSubAccount, params);
    return data;
  },
  getAccountLedgers: async (params) => {
    const data = await get(routes.getAccountLedgers, params);
    return data;
  },
  getAccountLedgersStatement: async (params) => {
    const data = await Statementget(routes.getAccountLedgers, params);
    return data;
  },
  getExportAccountLedgers: async (params) => {
    const data = await get(routes.getExportAccountLedgers, params);
    return data;
  },
  getExportAccountLedgersStatement: async (params) => {
    const data = await Statementget(routes.getExportAccountLedgers, params);
    return data;
  },
  getGeneralJournalLedgers: async (params) => {
    const data = await get(routes.getGeneralJournalLedgers, params);
    return data;
  },
  getVaultCustomers: async (params) => {
    const data = await get(routes.getVaultCustomers, params);
    return data;
  },
  getVaultCustomersWithOutToken: async (params) => {
    const data = await get(routes.getVaultCustomersWithOutToken, params);
    return data;
  },
  getPaymentAccounts: async (params) => {
    const data = await get(routes.getPaymentAccounts, params);
    return data;
  },
  getVendorTTPaymentAccounts: async (params) => {
    const data = await get(routes.getVendorTTPaymentAccounts, params);
    return data;
  },
  getPaymentAccountsWithOutToken: async (params) => {
    const data = await get(routes.getPaymentAccountsWithOutToken, params);
    return data;
  },
  getVaultTopUps: async (params) => {
    const data = await get(routes.getVaultTopUps, params);
    return data;
  },
  AccountBlance: async (params) => {
    const data = await get(routes.AccountBlance, params);
    return data;
  },
  createVaultTopUp: async (obj) => {
    const data = await post(routes.createVaultTopUp, obj);
    clearAccountingCaches();
    return data;
  },
  getVaultTopUpDetail: async (params) => {
    const data = await get(routes.getVaultTopUpDetail, params);
    return data;
  },
  getVaultTopUpPreview: async (params) => {
    const data = await get(routes.getVaultTopUpPreview, params);
    return data;
  },
  getAccountReports: async (params) => {
    const cacheKey = params?.to_date ?? "all";
    const cached = accountReportsCache.get(cacheKey);

    if (
      cached &&
      Date.now() - cached.timestamp < ACCOUNT_REPORTS_CACHE_TTL_MS
    ) {
      return cached.data;
    }

    const data = await get(routes.getAccountReports, params);
    accountReportsCache.set(cacheKey, { data, timestamp: Date.now() });
    return data;
  },
  getCashierActivity: async (params) => {
    const data = await get(routes.getCashierActivity, params);
    return data;
  },
  createJournalVoucher: async (obj) => {
    const data = await post(routes.createJournalVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  editJournalVoucher: async (obj) => {
    const data = await post(routes.editJournalVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  getJournalVouchers: async (params) => {
    const data = await get(routes.getJournalVouchers, params);
    return data;
  },
  getJournalVoucherTemplte: async (params) => {
    const data = await get(routes.getJournalVoucherTemplte, params);
    return data;
  },
  getJournalVoucherDetail: async (params) => {
    const data = await get(routes.getJournalVoucherDetail, params);
    return data;
  },
  getJournalVoucherPreview: async (params) => {
    const data = await get(routes.ExportJournalVoucherPreview, params);
    return data;
  },
  createFundTransferVoucher: async (obj) => {
    const data = await post(routes.createFundTransferVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  updateVaultTopUp: async (obj) => {
    const data = await patch(routes.updateVaultTopUp, obj);
    clearAccountingCaches();
    return data;
  },
  UpdateReceiptVoucher: async (obj) => {
    const data = await patch(routes.UpdateReceiptVoucher, obj);
    clearAccountingCaches();
    return data;
  },
  getFundTransferVouchers: async (params) => {
    const data = await get(routes.getFundTransferVouchers, params);
    return data;
  },
  getFundTransferVoucherPreview: async (params) => {
    const data = await get(routes.getFundTransferVoucherPreview, params);
    return data;
  },
  getFundTransferApproval: async (params) => {
    const data = await get(routes.getFundTransferApproval, params);
    return data;
  },
  FundTransferApprove: async (obj) => {
    const data = await post(routes.FundTransferApprove, obj);
    return data;
  },
  getFundTransferVoucherDetail: async (params) => {
    const data = await get(routes.getFundTransferVoucherDetail, params);
    return data;
  },
  getVehicleSumLedger: async (params) => {
    const data = await get(routes.getVehicleSumLedger, params);
    return data;
  },
  getVehicleSumLedgerV2: async (params) => {
    const data = await get(routes.getVehicleSumLedgerV2, params);
    return data;
  },
  getVehicleSumLedgerExport: async (params) => {
    const data = await get(routes.getVehicleSumLedgerExport, params);
    return data;
  },

  getVehicleSumLedgerStatement: async (params) => {
    const data = await Statementget(routes.getVehicleSumLedger, params);
    return data;
  },
  getAllAccountsData: async (params) => {
    const data = await get(routes.getAllAccountsData, params);
    return data;
  },
  deleteJournalVoucherTemplate: async (params) => {
    const data = await deleted(routes.deleteJournalVoucherTemplate, {}, params);
    return data;
  },
  editFundTransferVoucher: async (obj) => {
    const data = await put(routes.editFundTransferVoucher, obj);
    return data;
  },
  UpdatePaymentVoucherV2: async (obj) => {
    const data = await patch(routes.updatePaymentVoucherV2, obj);
    return data;
  },
  UpdateReceiptVoucherV2: async (obj) => {
    const data = await patch(routes.UpdateReceiptVoucherV2, obj);
    return data;
  },
  certifyLedger: async (obj) => {
    const data = await post(routes.certifyLedger, obj);
    return data;
  },
  getCertificationLedgers: async (params) => {
    const data = await get(routes.getCertificationLedgers, params);
    return data;
  },
};

export default FinanceServices;
